import axios from "axios";
import { addDays, format, isSameDay } from "date-fns";
import React, { useEffect, useState } from "react";
import { FaTrash } from "react-icons/fa";
import { API_URL } from "../../url/api";
import { convertISODateToLocalDate, convertLocalTimeToUTC } from "../../utils";
import {  parse, isValid,  } from 'date-fns';

interface AvailabilityMap {
  [date: string]: string[];
}

type BookedMap = {
  [date: string]: string[];
};

interface DoctorAvailability {
  id: number;
  date: string;
  slots: { id: number; datetime: string }[];
}

interface Doctor {
  id: number;
  name: string;
  description: string;
  age: string;
  image: string;
  user: number;
  availability: DoctorAvailability[];
}

type AvailableSlotsProps = {
  id: number;
  // date: string;
  // time: string;
  datetime: string;
};

const today = new Date();
const nextTenDays = Array.from({ length: 10 }, (_, index) =>
  addDays(today, index)
);

const formatDate = (date: string) => {
  const newdate = new Date(date);
  if (isSameDay(newdate, today)) {
    return "Today";
  } else if (isSameDay(newdate, addDays(today, 1))) {
    return "Tomorrow";
  } else {
    return format(newdate, "do MMMM");
  }
};

type AvailabilitySettingsProps = {
  doctorID: string;
};

const AvailabilitySettings: React.FC<AvailabilitySettingsProps> = ({
  doctorID,
}) => {
  const [isHovered, setIsHovered] = useState(0);

  const [upcomingTimeslot, setTimeslots] = useState<AvailableSlotsProps[]>();

  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);

  // const [availableSlots, setAvailableSlots] = useState<AvailableSlotsProps[]>();
  const [availableSlots, setAvailableSlots] = useState<AvailableSlotsProps[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [doctor, setDoctor] = useState<Doctor | null>(null);

  

  useEffect(() => {
    return () => {};
  }, [selectedSlots]);

  useEffect(() => {
    fetchDoctorAvailability();
    fetchUpcomingAppointmentsData();
  }, []);

  const fetchDoctorAvailability = async () => {
    try {
      const response = await fetch(`${API_URL}v2/doctors/${doctorID}/availability/`);
      const data = await response.json();
      setDoctor(data);
  
      let list: AvailableSlotsProps[] = [];
      data.availability.forEach((avail: DoctorAvailability) => {
        avail.slots.forEach((slot) => {
          list.push({
            id: slot.id,
            datetime: slot.datetime,
          });
        });
      });
      setAvailableSlots(list);
    } catch (error) {
      console.error("Error fetching doctor availability:", error);
    }
  };

  
  const fetchUpcomingAppointmentsData = async () => {
    try {
      const response = await axios.get(
        `${API_URL}v2/doctors/${doctorID}/upcoming-appointments/`
      );
      setTimeslots(
        response.data.appointment.map(
          (appointment: any) => appointment.time_slot
        )
      );
      console.log(
        "🚀 ~ fetchUpcomingAppointmentsData ~ response.data.appointment.map((appointment:any) => appointment.time_slot):",
        response.data.appointment.map(
          (appointment: any) => appointment.time_slot
        )
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

const handleDateSelect = (date: string) => {
    setSelectedDate(date);
    // setSelectedSlots(availability[date] || []);
  };



  const handleUpdateAvailability = async () => {
    const formattedData = {
      dates: [selectedDate],
      time_slots: [
        ...availableSlots
          .filter(slot => new Date(slot.datetime).toLocaleDateString("en-CA") === selectedDate)
          .map(slot => ({
            start_time: convertISODateToLocalDate(slot.datetime),
          })),
        ...selectedSlots.map((slotId) => ({
          start_time: convertISODateToLocalDate(
            convertLocalTimeToUTC(selectedDate, slotId)
          ),
        })),
      ],
    };

    try {
      const doc = localStorage.getItem("doctorId");
      const response = await fetch(
        `${API_URL}new-update-availability/${doc}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formattedData),
        }
      );

      if (response.ok) {
        console.log("Availability updated successfully");
        setSelectedSlots([]);
        fetchDoctorAvailability();
      } else {
        alert("Failed to update availability");
        console.error("Failed to update availability");
      }
    } catch (error) {
      alert("Error updating availability");
      console.error("Error updating availability:", error);
    }
  };

  const isSlotAvailable = (date: string, time: string) => {
    const selectedDateTime = parse(`${date} ${time}`, 'yyyy-MM-dd HH:mm:ss', new Date());
    
    return availableSlots.some(slot => {
      const slotDateTime = new Date(slot.datetime);
      return isSameDay(selectedDateTime, slotDateTime) && 
             format(slotDateTime, 'HH:mm:ss') === time;
    });
  };

  const handleSlotChange = (slot: string) => {
    if (isSlotAvailable(selectedDate, slot)) {
      // If the slot is already available, do nothing
      return;
    }

    const index = selectedSlots.indexOf(slot);
    if (index > -1) {
      setSelectedSlots(selectedSlots.filter((s) => s !== slot));
    } else {
      setSelectedSlots([...selectedSlots, slot]);
    }
  }

  const handleDeleteSlot = async (slotToDelete: AvailableSlotsProps) => {
    let confirmation = window.confirm(
      "Are you sure you want to delete this slot?"
    );
  
    if (!confirmation) return;
  
    // Remove the slot from the local state
    setAvailableSlots((prevSlots) => 
      prevSlots?.filter((slot) => slot.id !== slotToDelete.id)
    );
  
    const updatedSlots = availableSlots?.filter(
      (slot) => slot.id !== slotToDelete.id
    );
  
    const formattedData = {
      dates: [selectedDate],
      time_slots: updatedSlots?.map((slot) => ({
        start_time: convertISODateToLocalDate(slot.datetime),
      })),
    };
  
    try {
      const doc = localStorage.getItem("doctorId");
      const response = await fetch(
        `${API_URL}new-update-availability/${doc}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formattedData),
        }
      );
  
      if (response.ok) {
        console.log("Availability updated successfully");
        // No need to fetch again as we've already updated the local state
      } else {
        alert("Failed to update availability");
        console.error("Failed to update availability");
        // Revert the local state change if the server update fails
        fetchDoctorAvailability();
      }
    } catch (error) {
      alert("Error updating availability");
      console.error("Error updating availability:", error);
      // Revert the local state change if there's an error
      fetchDoctorAvailability();
    }
  };

  const today = new Date();
  const nextTenDays = Array.from({ length: 10 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index);
    return date.toISOString().split("T")[0];
  });

  const slots = [
    { label: "1 AM", value: "01:00:00" },
    { label: "2 AM", value: "02:00:00" },
    { label: "3 AM", value: "03:00:00" },
    { label: "4 AM", value: "04:00:00" },
    { label: "5 AM", value: "05:00:00" },
    { label: "6 AM", value: "06:00:00" },
    { label: "7 AM", value: "07:00:00" },
    { label: "8 AM", value: "08:00:00" },
    { label: "9 AM", value: "09:00:00" },
    { label: "10 AM", value: "10:00:00" },
    { label: "11 AM", value: "11:00:00" },
    { label: "12 PM", value: "12:00:00" },
    { label: "1 PM", value: "13:00:00" },
    { label: "2 PM", value: "14:00:00" },
    { label: "3 PM", value: "15:00:00" },
    { label: "4 PM", value: "16:00:00" },
    { label: "5 PM", value: "17:00:00" },
    { label: "6 PM", value: "18:00:00" },
    { label: "7 PM", value: "19:00:00" },
    { label: "8 PM", value: "20:00:00" },
    { label: "9 PM", value: "21:00:00" },
    { label: "10 PM", value: "22:00:00" },
    { label: "11 PM", value: "23:00:00" },
    { label: "12 AM", value: "00:00:00" },
  ];

  return (
    <div className="container mx-auto p-4">
      <h3 className="text-lg font-semibold mb-2">Availability:</h3>
      {selectedDate && (
        <div className="mb-12">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          {availableSlots &&
            availableSlots
              ?.filter(
                (x) =>
                  new Date(x.datetime).toLocaleDateString("en-CA") ===
                  selectedDate
              )
              .map((slot, index) => (
                <button
                  key={index}
                  onMouseEnter={() => setIsHovered(slot.id)}
                  onMouseLeave={() => setIsHovered(0)}
                  onClick={() => handleDeleteSlot(slot)}
                  className={`px-3 py-2 rounded  bg-primary text-white cursor-pointer transition-all duration-150 ease-in-out ${
                    isHovered == slot.id ? "scale-105 bg-red-500" : ""
                  }`}
                >
                  {isHovered == slot.id ? (
                    <div className="flex justify-center items-center">
                      <FaTrash size={24} />
                    </div>
                  ) : (
                    `${new Date(slot.datetime).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "numeric",
                      hour12: true,
                    })}`
                  )}
                </button>
              ))}
          </div>
          {(availableSlots?.length === 0 ||
            availableSlots?.filter(
              (x) =>
                new Date(x.datetime).toLocaleDateString("en-CA") ===
                selectedDate
            ).length === 0) && (
            <p className="text-gray-500">{`No availability set for ${selectedDate}`}</p>
          )}
        </div>
      )}

      <div className="mb-12">
        <h3 className="text-lg font-semibold mb-2">Set your availability:</h3>
        <p className="text-gray-500">
          Select a date to set your availability for the next 10 days
        </p>
        <br />
        <div className="flex flex-wrap gap-2">
          {nextTenDays.map((date) => {
            const formattedDate = format(date, "yyyy-MM-dd");
            return (
              <button
                key={formattedDate}
                // disabled={availability[selectedDate] && availability[selectedDate].length > 0}
                className={`px-3 py-2 rounded ${
                  selectedDate === formattedDate
                    ? "bg-primary text-white"
                    : "bg-gray-200"
                }`}
                onClick={() => handleDateSelect(formattedDate)}
              >
                {formatDate(date)}
              </button>
            );
          })}
        </div>
      </div>

      {selectedDate && (
        <div className="mb-12">
          <h3 className="text-lg font-semibold mb-2">Select Slots:</h3>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {slots.map((slot) => {
              const isAvailable = isSlotAvailable(selectedDate, slot.value);
              const slotDateTime = parse(`${selectedDate} ${slot.value}`, 'yyyy-MM-dd HH:mm:ss', new Date());
              const isExpired = isValid(slotDateTime) && new Date() > slotDateTime;
              const isUpcoming = upcomingTimeslot?.some(s => {
                const upcomingDateTime = new Date(s.datetime);
                return isSameDay(slotDateTime, upcomingDateTime) && 
                       format(upcomingDateTime, 'HH:mm:ss') === slot.value;
              });

              return (
                <button
                  key={slot.value}
                  disabled={isAvailable || isExpired || isUpcoming}
                  className={`
                    px-3 py-2 rounded 
                    ${isAvailable 
                      ? 'bg-green-600 text-white opacity-70 cursor-not-allowed' 
                      : isExpired || isUpcoming
                      ? 'bg-gray-400 text-gray-600 opacity-50 cursor-not-allowed'
                      : selectedSlots.includes(slot.value)
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 hover:bg-gray-300'
                    }
                    transition-all duration-200
                  `}
                  onClick={() => handleSlotChange(slot.value)}
                >
                  {isAvailable ? `${slot.label} - Available` : slot.label}
                </button>
              );
            })}
          </div>
        </div>
      )}


      {selectedSlots.length > 0 && (
        <button
          className="px-4 py-2 bg-primary text-white rounded"
          onClick={handleUpdateAvailability}
          disabled={!selectedDate}
        >
          Update Availability
        </button>
      )}
    </div>
  );
};

export default AvailabilitySettings;
